import { useEffect, useRef, useCallback } from 'react';

type ActivityTrackerOptions = {
  onInactive: () => void;
  timeoutDuration: number;
  checkInterval?: number;
};

export const useActivityTracker = ({
  onInactive,
  timeoutDuration,
  checkInterval = 30000,
}: ActivityTrackerOptions) => {
  const lastActivityRef = useRef<number>(Date.now());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef<boolean>(true);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const checkInactivity = useCallback(() => {
    if (!isActiveRef.current) return; // Prevent multiple triggers
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity >= timeoutDuration) {
      isActiveRef.current = false; // Prevent multiple calls
      onInactive();
    }
  }, [onInactive, timeoutDuration]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true; // Re-enable tracking
  }, []);

  // Single effect with stable dependencies
  useEffect(() => {
    // Reduce events - remove expensive mousemove and scroll
    const events = ['mousedown', 'keypress', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Set up interval
    intervalRef.current = setInterval(checkInactivity, checkInterval);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateActivity, checkInactivity, checkInterval]);

  return {
    resetActivity,
  };
};